import fp from 'lodash/fp'
import React from 'react'
import { combine, withContext, withCallbacks } from 'react-hooks-combine'
import { withFormModel } from 'shared/hooks'
import { spec, validators } from 'utils/validation'
import { StoreContext } from 'store/context'
import { ServicesContext } from 'services/context'
import { observer } from 'mobx-react'
import { showNotificationBasedOnDevice, redirectToProfileWithNotification } from 'utils/notification-helper'

import AddressComponent from './component'

const { string, any } = validators

// Create the form-enabled component
const AddressWithForm = combine({
	hooks: [
		withContext('appStore', StoreContext),
		withContext(
			'jurisdictionsService',
			ServicesContext,
			({ jurisdictionsService }) => jurisdictionsService
		),
		withFormModel((_state, { initialValues = {} }) => {
			const formattedCountry = initialValues.address?.country
				? { id: '', name: initialValues.address.country }
				: ''

			return {
				address1: {
					alias: 'address1',
					initialValue: initialValues.address?.address1 || '',
					spec: spec.flow(string.isString),
				},
				address2: {
					alias: 'address2',
					initialValue: initialValues.address?.address2 || '',
					spec: spec.flow(string.isString),
				},
				city: {
					alias: 'city',
					initialValue: initialValues.address?.city || '',
					spec: spec.flow(string.isString),
				},
				state: {
					alias: 'state',
					initialValue: initialValues.address?.state || '',
					spec: spec.flow(string.isString),
				},
				zip: {
					alias: 'zip',
					initialValue: initialValues.address?.zip || '',
					spec: spec.flow(string.isString),
				},
				country: {
					alias: 'country',
					initialValue: formattedCountry,
					spec: spec.flow(),
				},
			}
		}),

		withCallbacks({
			getOptionLabel:
				() =>
					({ name }) =>
						name,
			getOptionValue:
				() =>
					({ id }) =>
						id,

			loadJurisdictions:
				({ jurisdictionsService }) =>
					(search = '') =>
						jurisdictionsService.getJurisdictionsPublic({ search }),

			onSubmit:
				({ appStore }) =>
					async (data) => {
						const isMobile = window.innerWidth <= 767

						try {
							const loggedEntity = appStore?.auth?.loggedEntity
							if (!loggedEntity) {
								if (isMobile) {
									// Mobile: Show notification and redirect to profile (top notification only)
									redirectToProfileWithNotification(null, 'error', 'Changes unsaved', true)
								} else {
									// Desktop: Show toaster notification only
									showNotificationBasedOnDevice('error', 'Changes unsaved')
								}
								return
							}

							const countryName = data.country ? data.country.name : undefined
							const updateData = {
								address: {
									address1: data.address1,
									address2: data.address2,
									zip: data.zip,
									country: countryName,
									state: data.state,
									city: data.city,
								},
							}
							await loggedEntity.update(updateData)

							if (isMobile) {
								// Mobile: Show notification and redirect to profile (top notification only)
								redirectToProfileWithNotification(null, 'success', null, true)
							} else {
								// Desktop: Show toaster notification only
								showNotificationBasedOnDevice('success')
							}

						} catch (error) {
							if (isMobile) {
								// Mobile: Show notification and redirect to profile (top notification only)
								redirectToProfileWithNotification(null, 'error', 'Changes unsaved', true)
							} else {
								// Desktop: Show toaster notification only
								showNotificationBasedOnDevice('error', 'Changes unsaved')
							}
						}
					},
		}),
	],

	transformProps: ({
		appStore,
		form,
		onSubmit,
		getOptionLabel,
		getOptionValue,
		loadJurisdictions
	}) => {
		return {
			loggedEntity: appStore?.auth?.loggedEntity,
			form,
			onSubmit,
			getOptionLabel,
			getOptionValue,
			loadJurisdictions,
		}
	},
})(observer(AddressComponent))

// Wrapper component that provides initialValues
const AddressWrapper = combine({
	hooks: [
		withContext('appStore', StoreContext),
	],
	transformProps: ({ appStore }) => {
		return {
			initialValues: appStore?.auth?.loggedEntity || {},
		}
	},
})(({ initialValues }) => {
	return <AddressWithForm initialValues={initialValues} />
})

export default AddressWrapper 